/**
 * Foundational enums + locked literals shared across every other schema file in
 * this package. Plan §26.0. Every consumer MUST import enums from here — NEVER
 * derive aliases or merged unions. Three-axis tagging is deliberately three
 * INDEPENDENT enums (Kaiyang Lesson §25.6 / ADR-0002).
 */

import { z } from "zod";

export const RegulatoryDomain = z.enum([
  "Type Approval",
  "General Safety",
  "Emissions & CO2",
  "Battery & Materials",
  "Cybersecurity",
  "OTA & SUMS",
  "ADAS & Automation",
  "AI Regulation",
  "Privacy & Data",
  "Charging & Energy",
  "Member-State Operation",
  "RMI & Aftersales",
  "Market Surveillance & Recall",
  "Product Liability",
  "Market Rating",
  "Ecosystem Interop",
]);
export type RegulatoryDomain = z.infer<typeof RegulatoryDomain>;

export const ObligationKind = z.enum([
  "hard_law",
  "evidence_standard",
  "market_force",
  "ecosystem_signal",
]);
export type ObligationKind = z.infer<typeof ObligationKind>;

export const ObligationTiming = z.enum([
  "pre_market",
  "at_market_entry",
  "post_market",
  "recurring",
  "event_triggered",
  "phase_in",
]);
export type ObligationTiming = z.infer<typeof ObligationTiming>;

export const ApplicabilityCategory = z.enum([
  "applicable",
  "hidden_by_config",
  "unknown_needs_config",
]);
export type ApplicabilityCategory = z.infer<typeof ApplicabilityCategory>;

export const CoverageState = z.enum([
  "missing",
  "partial",
  "linked",
  "ready",
  "stale",
]);
export type CoverageState = z.infer<typeof CoverageState>;

export const LegalEffect = z.enum([
  "market_access_blocker",
  "registration_blocker",
  "in_use_restriction",
  "cost_penalty",
  "rating_penalty",
  "commercial_risk",
  "information_only",
]);
export type LegalEffect = z.infer<typeof LegalEffect>;

export const CitationGranularity = z.enum([
  "regulation",
  "article",
  "annex",
  "section",
  "chapter",
  "paragraph",
  "subparagraph",
]);
export type CitationGranularity = z.infer<typeof CitationGranularity>;

export const LifecycleLayer = z.enum(["A", "B", "C", "D", "P", "U"]);
export type LifecycleLayer = z.infer<typeof LifecycleLayer>;

export const LifecycleState = z.enum([
  "ACTIVE",
  "DRAFT",
  "PLACEHOLDER",
  "SHADOW",
  "SEED_UNVERIFIED",
  "ARCHIVED",
]);
export type LifecycleState = z.infer<typeof LifecycleState>;

export const VerificationMaturityTier = z.enum([
  "ACTIVE_VERIFIED",
  "ACTIVE_VERIFY_NOTE",
  "DRAFT",
  "SEED_UNVERIFIED",
]);
export type VerificationMaturityTier = z.infer<typeof VerificationMaturityTier>;

export const ValidationStatus = z.enum([
  "unvalidated",
  "auto-validated",
  "human-signed",
  "expired",
]);
export type ValidationStatus = z.infer<typeof ValidationStatus>;

export const ReviewTransitionState = z.enum([
  "candidate",
  "reviewed",
  "canonical",
  "blocked",
  "superseded",
  "deprecated",
]);
export type ReviewTransitionState = z.infer<typeof ReviewTransitionState>;

/**
 * Locked evidence-pack disclaimer (Kaiyang Lesson §25.12 / ADR-0002 red line).
 *
 * UI MUST render this exact string. CI `scripts/ci/disclaimer-forbidden-phrases.mjs`
 * forbids the strings "certified compliant", "fully compliant", "compliance verdict".
 * Replace this literal only via ADR + contract_version bump.
 */
export const EVIDENCE_PACK_DISCLAIMER =
  "This pack is a readiness view based on the cited registry snapshot and the configuration provided. Items marked `unknown_needs_config` cannot be classified without additional configuration input. This pack does not certify compliance, market access, or registration in any jurisdiction. Final regulatory decisions remain with the competent authorities and the OEM's qualified legal counsel." as const;
export type EvidencePackDisclaimer = typeof EVIDENCE_PACK_DISCLAIMER;

/**
 * Contract version of this schema bundle. Bumped together with semantic
 * schema changes; consumed by EvidencePackMeta.contractVersion. CI red line
 * #8 (contract-schema.mjs) gates PRs that drop this string.
 *
 * 0.1.1-w7b2 — adds EvidencePackInputSchema for the server-side
 * `/v1/evidence-pack/build` route (W7-slice-b2 / ADR-0005).
 * 0.1.2-a1-usecase — adds UseCaseEntrySchema + CoverageMatrixStatus for the
 * U band Use Case Intelligence catalog (A1 / ADR-0008 / RFC-0001).
 * 0.1.3-d1-promoted — adds PromotedRuleSchema for the reviewer-authored runtime
 * canonical store (D1 / ADR-0010 / RFC-0003).
 * 0.1.4-d2-provenance — adds ComplianceRequirementCard.provenanceTier for promoted-rule
 * board marking (D2 / ADR-0010 / RFC-0003).
 * 0.1.5-d4-caseintel — adds UseCasePublicSignal.useCaseId for the U "Case intelligence"
 * section (D4 / ADR-0008 / RFC-0003).
 * 0.1.6-ew-sourcechange — adds SourceChangeEventSchema for early-warning SENSE
 * (RFC-0004 Phase 1a / source-amendment detection over archived CELLAR sources).
 * 0.1.7-ew-tracking — adds ProgramRecord + Correction/Outcome event schemas for
 * the per-program obligation ledger (RFC-0004 Phase 1d TRACK).
 * 0.2.0-intel-spine — upgrades UseCasePublicSignal → structured IntelSignal
 * (typed intelType + topic/occurredAt/entity/jurisdiction/status, all optional
 * for back-compat) and extends SignalChangeKind with draft/proposed/market_voice
 * (RFC-0005 Phase A / ADR-0013).
 * 0.3.0-opportunity — adds OpportunityBriefSchema, the advisory cross-topic
 * opportunity/risk lens output (isVerdict:false, advisory only — ADR-0002)
 * (RFC-0005 Phase C / C1).
 * 0.4.0-ai-config — adds the `aiSystem` dimension to MarketEntryConfigSchema
 * (present/level/safetyComponent/dataFlags, nullable+optional for back-compat)
 * so later tasks can fire the EU AI Act rules (RFC-0006 R6-1).
 * 0.5.0-opportunity-diagnosis — adds the optional `opportunity.diagnosis` field
 * to OpportunityBriefSchema (regulatory_squeeze / riding_loosening /
 * compliance_headroom / watch / none): the content-anchored 机会/风险 classifier
 * (RFC-0008). Optional → back-compatible.
 */
export const CONTRACT_VERSION = "0.5.0-opportunity-diagnosis" as const;
